import { motion } from 'framer-motion';
import {
  Box,
  Typography,
  Grid,
  Chip,
  useTheme,
  Paper,
  styled,
  Button
} from '@mui/material';
import { GitHub } from '@mui/icons-material';
import React, { useMemo } from 'react';
import { getRandomProjectImage } from '../utils/imageGenerator.ts';

const ProjectCard = styled(Paper)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: '15px',
  transition: 'transform 0.3s',
  cursor: 'default'
}));

const projectsData = [
  // AI-first projects (prioritized)
  {
    title: "AI Legal Case Search",
    description: "Semantic legal research platform using Ollama + ChromaDB to synthesize case-law answers with citations.",
    tech: ['Python', 'Ollama', 'ChromaDB', 'Semantic Search', 'React'],
    github: ''
  },
  {
    title: "RAG API System with ChromaDB",
    description: "Retrieval-Augmented Generation API using ChromaDB for vector storage and semantic search.",
    tech: ['Python', 'ChromaDB', 'RAG', 'Embeddings'],
    github: ''
  },
  {
    title: "AI-Powered Code Review Workflow",
    description: "Automated code review pipeline leveraging Google Gemini to provide feedback on performance, accessibility, and security.",
    tech: ['Google Gemini', 'CI/CD', 'Prompt Engineering'],
    github: ''
  },
  {
    title: "Multi-Model LLM Exploration",
    description: "Experimentation and production integration across Claude, Gemini, and GPT-5 for model selection and agentic workflows.",
    tech: ['Claude', 'Gemini', 'GPT-5', 'Prompt Engineering'],
    github: ''
  },
  // Selected non-AI highlights
  {
    title: "ChatGPT Chat App",
    description: "Chat application integrating OpenAI for conversational experiences.",
    tech: ['Python'],
    github: 'https://github.com/shaileshyadav24/chatgpt-python'
  },
  {
    title: "Stock Trader",
    description: "Real-time stock trading UI with portfolio charts and live data feeds.",
    tech: ['NextJs', 'Tanstack', 'TypeScript'],
    github: 'https://github.com/shaileshyadav24/stock-trader'
  },
  {
    title: "Instagram WebApp",
    description: "Front-end clone showcasing authentication, feeds, and responsive layout.",
    tech: ['React', 'Redux'],
    github: 'https://github.com/shaileshyadav24/instagram-clone'
  }
];

const itemVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: (index: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: index * 0.2,
      duration: 0.6
    }
  })
};

const Projects = () => {
  const theme = useTheme();
  const isMobile = window.innerWidth <= 600;

  // Memoize image gradient to keep it consistent per project
  const projectImages = useMemo(() => {
    return projectsData.map((_, index) => getRandomProjectImage(index));
  }, []);

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Typography
          className="minimal-title gradient-text"
          variant="h2"
          sx={{
            textAlign: 'center',
            mb: 6,
            fontWeight: 700,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
            letterSpacing: '-1px',
          }}
        >
          Selected Work
        </Typography>
      </motion.div>

      <Grid container spacing={4}>
        {projectsData.map((project, index) => (
          <Grid item xs={12} sm={6} md={4} key={project.title}>
            <motion.div
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              custom={index}
            >
              <ProjectCard elevation={4} sx={{
                background: '#fff',
                border: '1px solid #e3f2fd',
                boxShadow: '0 2px 12px rgba(21,101,192,0.06)',
                minHeight: 380,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                transition: 'box-shadow 0.2s',
                overflow: 'hidden',
                '&:hover': {
                  boxShadow: '0 6px 24px rgba(21,101,192,0.12)',
                  borderColor: '#2196f3',
                },
              }}>
                <Box sx={{
                  width: '100%',
                  height: 120,
                  background: projectImages[index],
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '3rem',
                  fontFamily: 'system-ui'
                }}>
                  {['🤖', '⚛️', '🔷', '🐍', '🚀', '📊', '🎨'][index % 7]}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#1565c0', textAlign: 'center' }}>
                  {project.title}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: '#333', textAlign: 'center' }}>
                  {project.description}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mb: 2 }}>
                  {project.tech.map(tech => (
                    <Chip
                      key={tech}
                      label={tech}
                      size="small"
                      sx={{
                        bgcolor: '#e3f2fd',
                        color: '#1565c0',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                        borderRadius: 2,
                        boxShadow: '0 1px 4px rgba(21,101,192,0.06)',
                      }}
                    />
                  ))}
                </Box>
                {/* <Box sx={{ mb: 3 }}>
                  <Button href={project.github} target="_blank" size="small" startIcon={<GitHub />}>Source</Button>
                </Box> */}
              </ProjectCard>
            </motion.div>
          </Grid>
        ))}
      </Grid>
      {/* Modal removed — simplified one-page showcase */}
    </Box>
  );
};

export default Projects;
